import { useState } from 'react';
import { useDatos } from '../datos/DatosContext';
import { useAviso, useRetardo } from '../lib/avisos';
import { nuevoId } from '../lib/fechas';
import { NOMBRES_CONTEXTO, respuestaConversacion, type ClaveContexto } from '../datos/simulaciones';
import { Icono } from '../componentes/ui/Icono';
import { BloqueIA, Chip, Demo, Pensando } from '../componentes/ui/basicos';

export function Reflexiones() {
  const { datos, agregar, actualizar } = useDatos();
  const aviso = useAviso();
  const retardo = useRetardo();
  const [contexto, setContexto] = useState<Record<ClaveContexto, boolean>>({ suenos: true, biblioteca: true, diario: false, cursos: false });
  const [texto, setTexto] = useState('');
  const [ocupada, setOcupada] = useState(false);

  // La conversación se muestra de la más antigua a la más reciente (en datos se guarda al revés).
  const mensajes = [...datos.conversacion].reverse();

  const cuentas: Record<ClaveContexto, string> = {
    suenos: datos.suenos.filter((d) => d.permitirIA).length + ' de ' + datos.suenos.length,
    biblioteca: datos.biblioteca.filter((b) => b.permitirIA).length + ' de ' + datos.biblioteca.length,
    diario: datos.diario.filter((d) => d.permitirIA).length + ' de ' + datos.diario.length,
    cursos: 'sin permisos'
  };

  const enviar = () => {
    const t = texto.trim();
    if (!t) { aviso('Escribe una pregunta.'); return; }
    if (ocupada) return;
    const ctx = { ...contexto };
    agregar('conversacion', { id: nuevoId('m'), autora: 'yo', texto: t });
    setTexto('');
    setOcupada(true);
    retardo(() => {
      agregar('conversacion', { id: nuevoId('m'), autora: 'ia', guardado: 'no', bloques: respuestaConversacion(ctx) });
      setOcupada(false);
    }, 1300);
  };

  return (
    <div className="stack-lg">
      <header>
        <p className="eyebrow">Conversar conmigo</p>
        <h1 className="display">Reflexiones con IA</h1>
        <p className="muted" style={{ margin: '6px 0 0' }}>La IA solo ve lo que eliges aquí, y solo los registros con permiso.</p>
      </header>
      <section className="card stack-sm">
        <span className="lbl">Contexto de esta conversación</span>
        <div className="chips" role="group" aria-label="Contexto autorizado">
          {(Object.keys(NOMBRES_CONTEXTO) as ClaveContexto[]).map((k) => (
            <Chip key={k} activo={contexto[k]} onClick={() => setContexto((c) => ({ ...c, [k]: !c[k] }))}>
              {NOMBRES_CONTEXTO[k]} <span className="muted small">{cuentas[k]}</span>
            </Chip>
          ))}
          <button className="chip locked" disabled><Icono nombre="lock" tam="sm" />Terapia · excluida siempre</button>
        </div>
      </section>

      <div className="chat" aria-live="polite">
        {mensajes.map((m) =>
          m.autora === 'yo' ? (
            <div key={m.id} className="msg-yo">{m.texto}</div>
          ) : (
            <div key={m.id} className="msg-ia">
              {(m.bloques ?? []).map((b, i) => <BloqueIA key={i} tipo={b.tipo} etiqueta={b.etiqueta}>{b.texto}</BloqueIA>)}
              <div className="row">
                {m.guardado === 'no' ? <button className="btn small" onClick={() => actualizar('conversacion', m.id, { guardado: 'confirmando' })}>Guardar esta reflexión</button> : null}
                {m.guardado === 'confirmando' ? (
                  <>
                    <span className="small">Se guardará aparte y no cambia tus registros. ¿Confirmas?</span>
                    <button className="btn primary small" onClick={() => { actualizar('conversacion', m.id, { guardado: 'si' }); aviso('Guardada en Reflexiones con IA (solo en esta sesión).'); }}>Sí, guardar</button>
                    <button className="btn quiet small" onClick={() => actualizar('conversacion', m.id, { guardado: 'no' })}>No</button>
                  </>
                ) : null}
                {m.guardado === 'si' ? <span className="tag moss"><Icono nombre="check" tam="sm" />Guardada · aprobada por ti</span> : null}
              </div>
            </div>
          )
        )}
        {ocupada ? <Pensando>Buscando solo en el contexto elegido…</Pensando> : null}
      </div>

      <div className="composer">
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="chatin" className="sr">Escribe tu pregunta</label>
          <textarea id="chatin" rows={2} placeholder="Pregunta algo sobre tus registros autorizados…" value={texto} onChange={(e) => setTexto(e.target.value)} />
        </div>
        <button className="btn primary" onClick={enviar}>Enviar</button>
      </div>
      <p className="muted small" style={{ margin: '-12px 0 0' }}><Demo>Simulado</Demo> Las respuestas son de ejemplo; no hay ninguna IA conectada.</p>
    </div>
  );
}
